import { makeAutoObservable, runInAction } from 'mobx'
import { injectable, inject, decorate } from 'inversify'
import { debounce, uniqBy } from 'lodash'

// evertel
import { Api } from '@evertel/api'
import { APIDataMedia } from '@evertel/types'
import { NormalizedMedia } from '../NormalizedMediaType'
import { APIDataMediaToNormalizedMedia } from '../APIDataMediaToNormalizedMedia'

class ThreadMediaSearchController {
    media: NormalizedMedia[] = []
    mediaLatestFetch: NormalizedMedia[] = []
    threadId = 0
    searchTerm = ''
    mimeType = ''
    page = 1
    limit = 10
    mediaCount = 0
    isInfiniteScroll = true
    isLoading = false

    constructor(
        private api: Api
    ) {
        makeAutoObservable(this)
    }

    init(threadId: number, isInfiniteScroll = true) {
        this.threadId = threadId
        this.isInfiniteScroll = isInfiniteScroll
        this.resetState()
    }

    setSearchTerm(value: string) {
        this.resetSearchMeta()
        this.searchTerm = value
    }

    setMimetype(value: string) {
        this.resetSearchMeta()
        this.mimeType = value
    }

    setPage(page: number) {
        this.page = page
    }

    debouncedSearch = async () => {
        return debounce(async () => this.search(), 300)
    }

    search = async () => {
        if (!this.threadId) return

        runInAction(() => {
            this.isLoading = true
        })

        // need to grab these here because of async issues that can happen after the first await
        const localFilter = this.filter
        const localSearchTerm = this.searchTerm
        const localThreadId = this.threadId
        const localPage = this.page

        let count = this.mediaCount
        let results: APIDataMedia[] = []

        // grab the total count of media in this thread
        if (!this.mediaCount) {
            count = await this.fetchMediaCount(localFilter.where)
        }

        runInAction(() => {
            this.mediaLatestFetch = []
        })

        // stop from fetching if we already got them all
        if (count > this.media?.length) {
            results = await this.api.Routes.Thread.getMedia(localThreadId, localFilter)
        }

        const normalizedResults = results.map(m => APIDataMediaToNormalizedMedia(m))
        const apiDataResults = (this.isInfiniteScroll) ? uniqBy([...this.media, ...normalizedResults], 'id') : normalizedResults

        // Check if parameters have changed before updating state
        if (
            this.searchTerm !== localSearchTerm ||
            this.threadId !== localThreadId
        ) {
            // Parameters have changed; discard stale results
            return
        }

        runInAction(() => {
            this.mediaLatestFetch = normalizedResults
            this.media = apiDataResults
            this.isLoading = false
        })
    }

    fetchNextMedia() {
        this.page = this.page + 1
        this.search()
    }

    async fetchMediaCount(where = this.filter.where) {
        const res = await this.api.Routes.Thread.getMediaCount(this.threadId, where)

        runInAction(() => {
            this.mediaCount = res?.count ?? 0
        })

        return res?.count ?? 0
    }

    resetSearchMeta() {
        // This runInAction should NOT be necessary, but RN complains
        runInAction(() => {
            this.page = 1
            this.media = []
            this.mediaCount = 0
        })
    }

    resetState() {
        this.resetSearchMeta()
        this.searchTerm = ''
        this.mimeType = ''
    }

    get filter() {
        const f = {
            where: {},
            order: 'updatedDate DESC',
            limit: this.limit,
            skip: (this.page - 1) * this.limit
        }

        if (this.searchTerm !== '') {
            f.where['fileName'] = { like: `%${this.searchTerm}%` }
        }

        return f
    }

    get images() {
        return this.media?.filter(m => m.mediaType === 'image')
    }
}

decorate(injectable(), ThreadMediaSearchController)
decorate(inject(Api), ThreadMediaSearchController, 0)

export { ThreadMediaSearchController }
